import { Component, h, Host, Prop, State, forceUpdate } from "@stencil/core";
import {
    ContentDataProvider,
    ContentDataProviderState,
    getContentDataProvider,
} from "../../../apis/stateful-content";
import { TrainingItem, TrainingGroup } from "./stateful-trainings-interface";
import { i18nProvider } from "@trumpf/ux.i18n";

@Component({ tag: "ux-iws-stateful-trainings", shadow: false })
export class UxIwsStatefulTrainings {
    private dataProvider: ContentDataProvider<TrainingItem>;

    /** scope used to retrieve the content data provider */
    @Prop() scope: string = "";
    /** endpoint for content data */
    @Prop() url: string = "";
    /** optional language for translations (mainly used for storybook) - otherwise language is automatically detected by ux.i18n */
    @Prop() language?: string = "";
    /** namespace that's used for translations */
    @Prop() translationsNamespace: string = "";

    @State() groups: TrainingGroup[] = [];

    @State() selectedTrainingItem: TrainingItem;
    @State() selectedTrainingItemEvent: string[] = [];

    private i18nSubscriberId: string;

    connectedCallback() {
        this.i18nSubscriberId = i18nProvider.subscribe(() => forceUpdate(this));
    }

    disconnectedCallback() {
        i18nProvider.unsubscribe(this.i18nSubscriberId);
    }

    componentWillLoad() {
        this.dataProvider = getContentDataProvider<TrainingItem>(this.scope);
        this.dataProvider.subscribe(this.handleDataProviderState);

        this.handleDataProviderState(this.dataProvider.state);

        if (this.url !== "") {
            this.dataProvider.setEndpoint(this.url);
        }

        this.groups.forEach((group) => {
            group.items.forEach((item) => {
                item.hasChanged = false;
            });
        });

        if (this.language !== "") {
            i18nProvider.changeLanguage(this.language);
        }
    }

    private handleDataProviderState = ({ data }: ContentDataProviderState) => {
        const { t } = i18nProvider;

        if (data === null) {
            return;
        }
        const groups: TrainingGroup[] = [];
        data.items.forEach((item) => {
            const group = groups.find(
                ({ category }) => category === item.category
            );
            if (group === undefined) {
                const groupTitle = t(
                    `${this.translationsNamespace}:content.category.values.${item.category}`
                );
                groups.push({
                    label: groupTitle,
                    category: item.category,
                    items: [item],
                });
            } else {
                group.items.push(item);
            }
        });
        this.groups = groups.sort(this.sortGroups);
    };

    private sortGroups = (a: TrainingGroup, b: TrainingGroup): number => {
        const sortArray: Array<number | string> | null =
            this.dataProvider.data?.schema?.category?.order || null;
        if (Array.isArray(sortArray)) {
            return (
                sortArray.indexOf(a.category) - sortArray.indexOf(b.category)
            );
        }
        return 0;
    };

    render() {
        const { language } = i18nProvider;
        const { pending, error } = this.dataProvider;
        const hasData = Array.isArray(this.groups) && this.groups.length > 0;

        const expandedCategories =
            this.dataProvider.data?.schema?.isExpanded || [];

        return (
            <Host lang={language}>
                <ux-spinner-overlay {...(pending ? { opened: true } : {})} />
                {hasData &&
                    this.groups.map((group) => {
                        const isExpanded = expandedCategories.includes(
                            group.category
                        );

                        return (
                            <ux-accordion
                                key={group.category}
                                isExpanded={isExpanded}
                            >
                                <h3
                                    slot="title"
                                    class="ux-text-title ux-text-title--light ux-text-link--green"
                                >
                                    {group.label || group.category} (
                                    {group.items.length})
                                </h3>
                                {group.items.map((item) => (
                                    <ux-iws-stateful-training-item
                                        item={item}
                                        translationsNamespace={
                                            this.translationsNamespace
                                        }
                                    />
                                ))}
                            </ux-accordion>
                        );
                    })}
                {!hasData && !pending && error && (
                    <div class="ux-u-d-flex ux-u-flex-dir-row ux-u-flex-ai-center">
                        <span
                            class="ux-u-mr-xs ux-u-mr-lg-l"
                            style={{ fill: "#d8d8d8" }}
                        >
                            <ux-icon
                                name="cross"
                                size-s="53"
                                size-m="53"
                                size-l="56"
                            ></ux-icon>
                        </span>
                        {error.head && (
                            <h3 class="ux-text-headline ux-text-headline--big ux-u-my-zero">
                                {error.head}
                            </h3>
                        )}
                        {(error.copy || error.message) && (
                            <p class="ux-text-normal ux-text-normal--light-grey ux-u-mt-xxxs">
                                {error.copy || error.message}
                            </p>
                        )}
                    </div>
                )}
            </Host>
        );
    }
}
